/**
 * MarketMonitorLiveClient
 *
 * Thin WebSocket client for the apigateway endpoint at
 * `/ws/market-monitor/stream` (see [crates/apigateway/src/live/routes/market_monitor_stream.rs]).
 *
 * Wire protocol matches `market_monitor_live::wire::ClientOp` /
 * `market_monitor_live::wire::ServerFrame` exactly. Result payloads use the
 * same `DslResultPayload` shape as today's `/api/dsl/execute` HTTP path so
 * `MonitorChart` can decode them with the existing helpers.
 */

import config from "../../../config/api";
import type { DslResultPayload } from "../../../services/DslService";

export type TickCadence =
  | "seconds_1"
  | "seconds_5"
  | "seconds_10"
  | "seconds_30"
  | "minutes_1"
  | "minutes_5"
  | "minutes_30"
  | "hours_1";

export const TICK_CADENCE_OPTIONS: Array<{ value: TickCadence; label: string }> = [
  { value: "seconds_1", label: "1 sec" },
  { value: "seconds_5", label: "5 sec" },
  { value: "seconds_10", label: "10 sec" },
  { value: "seconds_30", label: "30 sec" },
  { value: "minutes_1", label: "1 min" },
  { value: "minutes_5", label: "5 min" },
  { value: "minutes_30", label: "30 min" },
  { value: "hours_1", label: "1 hour" },
];

export const DEFAULT_TICK_CADENCE: TickCadence = "seconds_10";

interface TagSpec {
  tag_id: string;
  dsl: string;
}

type ClientOp =
  | { op: "hello"; cadence: TickCadence }
  | { op: "set_cadence"; cadence: TickCadence }
  | { op: "subscribe"; tag_id: string; dsl: string }
  | { op: "unsubscribe"; tag_id: string }
  | { op: "switch_dashboard"; tags: TagSpec[] }
  | { op: "resubscribe"; tag_id: string };

export interface ServerErrorPayload {
  code: string;
  message: string;
}

export interface TailSample {
  date: string;
  value: number;
}

export type ServerFrame =
  | { type: "hello_ack"; cadence: TickCadence; supported: TickCadence[] }
  | { type: "cadence_ack"; cadence: TickCadence }
  | {
      type: "seed";
      tag_id: string;
      seed_version: number;
      result: DslResultPayload;
    }
  | {
      type: "delta";
      tag_id: string;
      seed_version: number;
      appended: TailSample[];
      tail_hash: number;
    }
  | { type: "scalar"; tag_id: string; result: DslResultPayload }
  | { type: "error"; tag_id: string; error: ServerErrorPayload }
  | { type: "stale"; tag_id: string; since: string };

export type SeedListener = (
  tagId: string,
  seedVersion: number,
  result: DslResultPayload,
) => void;
export type DeltaListener = (
  tagId: string,
  seedVersion: number,
  appended: TailSample[],
  tailHash: number,
) => void;
export type ScalarListener = (tagId: string, result: DslResultPayload) => void;
export type ErrorListener = (tagId: string, error: ServerErrorPayload) => void;
export type StaleListener = (tagId: string, since: string) => void;
export type StatusListener = (status: ConnectionStatus) => void;

export type ConnectionStatus =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed";

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15000;

export class MarketMonitorLiveClient {
  private url: string;
  private ws: WebSocket | null = null;
  private status: ConnectionStatus = "idle";
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;

  private cadence: TickCadence = DEFAULT_TICK_CADENCE;
  private subscriptions: Map<string, string> = new Map(); // tag_id -> dsl

  private seedListener: SeedListener | null = null;
  private deltaListener: DeltaListener | null = null;
  private scalarListener: ScalarListener | null = null;
  private errorListener: ErrorListener | null = null;
  private staleListener: StaleListener | null = null;
  private statusListener: StatusListener | null = null;

  constructor(url: string = config.marketMonitorLiveWsUrl) {
    this.url = url;
  }

  /** Open the WS and send the initial `hello`. Idempotent. */
  open(cadence: TickCadence = this.cadence): void {
    this.cadence = cadence;
    this.intentionallyClosed = false;
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    this.connect();
  }

  /** Close the WS and stop any pending reconnect. */
  close(): void {
    this.intentionallyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
    this.setStatus("closed");
  }

  setCadence(cadence: TickCadence): void {
    this.cadence = cadence;
    this.send({ op: "set_cadence", cadence });
  }

  subscribe(tagId: string, dsl: string): void {
    this.subscriptions.set(tagId, dsl);
    this.send({ op: "subscribe", tag_id: tagId, dsl });
  }

  unsubscribe(tagId: string): void {
    this.subscriptions.delete(tagId);
    this.send({ op: "unsubscribe", tag_id: tagId });
  }

  // Tell the server to clear its delta cursor for `tagId`; next eval re-sends a Seed.
  resubscribe(tagId: string): void {
    this.send({ op: "resubscribe", tag_id: tagId });
  }

  /**
   * Replace the current subscription set atomically. Server runs
   * `register` for new tag ids first (acquire), then `unregister` for
   * removed ones (release) — see `market_monitor_stream::switch_dashboard`.
   */
  switchDashboard(tags: Array<{ tagId: string; dsl: string }>): void {
    this.subscriptions.clear();
    for (const t of tags) {
      this.subscriptions.set(t.tagId, t.dsl);
    }
    this.send({
      op: "switch_dashboard",
      tags: tags.map((t) => ({ tag_id: t.tagId, dsl: t.dsl })),
    });
  }

  onSeed(fn: SeedListener): void {
    this.seedListener = fn;
  }

  onDelta(fn: DeltaListener): void {
    this.deltaListener = fn;
  }

  onScalar(fn: ScalarListener): void {
    this.scalarListener = fn;
  }

  onError(fn: ErrorListener): void {
    this.errorListener = fn;
  }

  onStale(fn: StaleListener): void {
    this.staleListener = fn;
  }

  onStatusChange(fn: StatusListener): void {
    this.statusListener = fn;
  }

  getStatus(): ConnectionStatus {
    return this.status;
  }

  private connect(): void {
    this.setStatus(this.reconnectAttempt > 0 ? "reconnecting" : "connecting");
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (e) {
      console.error("[market-monitor-live] WS construction failed", e);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      this.setStatus("open");
      // Send hello + replay subscriptions on (re)connect.
      this.sendDirect({ op: "hello", cadence: this.cadence });
      for (const [tagId, dsl] of this.subscriptions.entries()) {
        this.sendDirect({ op: "subscribe", tag_id: tagId, dsl });
      }
    });

    ws.addEventListener("message", (event) => {
      let frame: ServerFrame;
      try {
        frame = JSON.parse(event.data as string) as ServerFrame;
      } catch (e) {
        console.warn("[market-monitor-live] dropped unparseable frame", e);
        return;
      }
      this.handleFrame(frame);
    });

    ws.addEventListener("close", () => {
      this.ws = null;
      if (!this.intentionallyClosed) {
        this.scheduleReconnect();
      }
    });

    ws.addEventListener("error", (e) => {
      console.warn("[market-monitor-live] socket error", e);
    });
  }

  private scheduleReconnect(): void {
    if (this.intentionallyClosed) return;
    const delay = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * 2 ** Math.min(this.reconnectAttempt, 6),
    );
    this.reconnectAttempt += 1;
    this.setStatus("reconnecting");
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private handleFrame(frame: ServerFrame): void {
    switch (frame.type) {
      case "hello_ack":
      case "cadence_ack":
        return;
      case "seed":
        this.seedListener?.(frame.tag_id, frame.seed_version, frame.result);
        return;
      case "delta":
        this.deltaListener?.(
          frame.tag_id,
          frame.seed_version,
          frame.appended,
          frame.tail_hash,
        );
        return;
      case "scalar":
        this.scalarListener?.(frame.tag_id, frame.result);
        return;
      case "error":
        this.errorListener?.(frame.tag_id, frame.error);
        return;
      case "stale":
        this.staleListener?.(frame.tag_id, frame.since);
        return;
    }
  }

  private send(op: ClientOp): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      // Not connected yet — replayed from `subscriptions` on next `open`.
      return;
    }
    this.sendDirect(op);
  }

  private sendDirect(op: ClientOp): void {
    if (!this.ws) return;
    try {
      this.ws.send(JSON.stringify(op));
    } catch (e) {
      console.warn("[market-monitor-live] send failed", e);
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.statusListener?.(status);
  }
}
